import { App, Form, type FormInstance, Modal } from 'antd';
import { useEffect } from 'react';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type {
    ProdutoBodyEstoque,
    ProdutoBodyPacoteBarra,
} from '@/features/produto/types/ProdutoBody';
import type { ProdutoUnidade } from '@/features/produto/types/ProdutoUnidade';
import type { Dialog } from '@/std/dialog/types';
import { useQueryProdutoLocalEstoqueGet } from '@/features/produto-local-estoque/api/apiProdutoLocalEstoqueGet';
import { ProdutoLocalEstoqueStatus } from '@/features/produto-local-estoque/types/enum';
import { ProdutoPacoteBarraFormFields } from '@/features/produto/components/ProdutoPacoteBarraFormFields';

export function ProdutoTabEstoquePacoteModal({
    dialog,
    form,
    setFormDirty,
}: {
    dialog: Dialog<ProdutoBodyPacoteBarra>;
    form: FormInstance<FormProdutoValues>;
    setFormDirty: (dirty: boolean) => void;
}) {
    const { notification } = App.useApp();

    const [formPacote] = Form.useForm<ProdutoBodyPacoteBarra>();

    const produto_unidade: ProdutoUnidade | null = Form.useWatch('produto_unidade', form);
    const produtoEstoque: ProdutoBodyEstoque[] = Form.useWatch('produto_estoque', form) ?? [];

    const { data: locaisData } = useQueryProdutoLocalEstoqueGet();

    const locaisEstoque = (locaisData?.registros ?? []).filter(
        (local) =>
            local.est_status === ProdutoLocalEstoqueStatus.Ativado &&
            local.est_permite_venda === 'S' &&
            produtoEstoque.some(
                (estoque) => estoque.excluir !== 'S' && estoque.pre_estoque_idpk === local.est_idpk,
            ),
    );

    const pro_preco_venda = Form.useWatch('pro_preco_venda', form) || 0;
    const pro_cod_barra = Form.useWatch('pro_cod_barra', form);

    const onFinish = (): void => {
        const values = formPacote.getFieldsValue(true);

        const produtoPacoteBarra: ProdutoBodyPacoteBarra[] =
            form.getFieldValue('produto_pacote_barra') || [];

        const key = dialog.state.data?.key;

        if (dialog.state.mode === 'edit' && key) {
            const pacoteIndex = produtoPacoteBarra.findIndex((p) => p.key === key);

            if (pacoteIndex === -1) {
                notification.error({ title: 'Pacote de barras não encontrado' });
                return;
            }

            const newProdutoPacoteBarra = [...produtoPacoteBarra];
            newProdutoPacoteBarra[pacoteIndex] = values;
            form.setFieldValue('produto_pacote_barra', newProdutoPacoteBarra);
        }

        if (dialog.state.mode === 'add') {
            form.setFieldValue('produto_pacote_barra', [...produtoPacoteBarra, values]);
        }

        setFormDirty(true);
        dialog.actions.close();
    };

    useEffect(() => {
        if (dialog.state.open) {
            formPacote.resetFields();

            if (dialog.state.data) {
                formPacote.setFieldsValue(dialog.state.data);
            }
        }
    }, [dialog.state.open, dialog.state.data, formPacote]);

    return (
        <Modal
            onCancel={() => dialog.actions.close()}
            onOk={() => formPacote.submit()}
            open={dialog.state.open}
            title={`${dialog.state.mode === 'add' ? 'Adicionar' : 'Editar'} pacote de barras`}
        >
            <Form form={formPacote} layout='vertical' onFinish={onFinish}>
                <ProdutoPacoteBarraFormFields
                    locaisEstoque={locaisEstoque}
                    pro_cod_barra={pro_cod_barra}
                    pro_preco_venda={pro_preco_venda}
                    unidadeEstoque={produto_unidade?.pru_idpk}
                />
            </Form>
        </Modal>
    );
}
